import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { GuardData } from '../service/guardService';

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[2]};
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.primary};
  cursor: pointer;
  padding: ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.md};
  transition: background 0.2s ease;

  &:hover {
    background: ${theme.colors.light.background.secondary};
  }
`;

const GuardInfo = styled.div`
  margin-bottom: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.md};
`;

const GuardName = styled.h4`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const GuardId = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  margin: 0;
`;

interface StatusModalProps {
  guard: GuardData;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (guard: GuardData, isActive: boolean, reason: string, inactiveDate: string) => void;
}

const StatusModal: React.FC<StatusModalProps> = ({ guard, isOpen, onClose, onConfirm }) => {
  const [inactiveReason, setInactiveReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [inactiveDate, setInactiveDate] = useState(new Date().toISOString().split('T')[0]);

  const handleSubmit = () => {
    if (!inactiveReason) {
      return;
    }

    if (!inactiveDate) {
      return;
    }

    const finalReason = inactiveReason === 'other' ? customReason : inactiveReason;
    if (inactiveReason === 'other' && !customReason.trim()) {
      return;
    }

    onConfirm(guard, false, finalReason, inactiveDate);
    setInactiveReason('');
    setCustomReason('');
    setInactiveDate(new Date().toISOString().split('T')[0]);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Deactivate Guard"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit}
            disabled={!inactiveReason || (inactiveReason === 'other' && !customReason.trim()) || !inactiveDate}
          >
            Deactivate Guard
          </Button>
        </>
      }
    >
      <GuardInfo>
        <GuardName>{guard.name || '—'}</GuardName>
        <GuardId>ID: {guard.guardId || guard.id}</GuardId>
      </GuardInfo>

      <FormGroup>
        <Input
          label="Inactive Date"
          type="date"
          value={inactiveDate}
          onChange={(e) => setInactiveDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          dark
          fullWidth
          required
        />
      </FormGroup>

      <FormGroup>
        <label style={{ 
          display: 'block', 
          fontSize: theme.typography.fontSize.sm, 
          fontWeight: theme.typography.fontWeight.bold,
          color: theme.colors.light.text.primary,
          marginBottom: theme.spacing[2]
        }}>
          Reason for Deactivation:
        </label>
        <RadioGroup>
          <RadioLabel>
            <input
              type="radio"
              name="inactiveReason"
              value="leave"
              checked={inactiveReason === 'leave'}
              onChange={(e) => setInactiveReason(e.target.value)}
            />
            Leave
          </RadioLabel>
          <RadioLabel>
            <input
              type="radio"
              name="inactiveReason"
              value="terminate"
              checked={inactiveReason === 'terminate'}
              onChange={(e) => setInactiveReason(e.target.value)}
            />
            Terminate
          </RadioLabel>
          <RadioLabel>
            <input
              type="radio"
              name="inactiveReason"
              value="death"
              checked={inactiveReason === 'death'}
              onChange={(e) => setInactiveReason(e.target.value)}
            />
            Death
          </RadioLabel>
          <RadioLabel>
            <input
              type="radio"
              name="inactiveReason"
              value="other"
              checked={inactiveReason === 'other'}
              onChange={(e) => setInactiveReason(e.target.value)}
            />
            Other
          </RadioLabel>
        </RadioGroup>
      </FormGroup>

      {inactiveReason === 'other' && (
        <FormGroup>
          <Input
            label="Specify Reason"
            placeholder="Enter custom reason..."
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            dark
            fullWidth
            required
          />
        </FormGroup>
      )}
    </Modal>
  );
};

export default StatusModal;

